import { Context } from 'chartjs-plugin-datalabels';
import dayjs from 'dayjs';
import { useEffect, useMemo, useState } from 'react';

import { EmployeeChartData } from '~hooks/query/company/useCompanyEmployeeChart';
import useMedia from '~hooks/useMedia';
import { setGtmDataLayer } from '~utils/common/thirdPartyEvent';

import {
  DEFAULT_CHART_STYLE,
  DEFAULT_OPTIONS,
  LINE_STYLE,
  POINT_STYLE,
  TOOLTIP_CONNECT_DASH,
  TOOLTIP_LINE_PLUGINS,
} from './constants';
import {
  getBarChartSizeProps,
  getDataByQuarter,
  getLabelsByPediod,
  getQuarter,
  getSliceData,
} from './helper';
import { AvailableYears } from './types';

type DisplayMode = 'month' | 'quarter' | 'year';
interface DisplayModeProps {
  labelsPeriod: 3 | 6 | 12;
  datasetDisplayMode: DisplayMode;
}

interface UseChartProps {
  originData: EmployeeChartData[];
  year: AvailableYears;
  externalTooltipHandler: (context: Context) => void;
}

interface PonitStyle {
  pointStyle: string;
  pointBorderColor: string;
  pointRadius: number;
  pointHoverRadius: number;
  pointHoverBorderWidth: number;
  pointHoverBackgroundColor: string;
  pointHoverRdius?: number;
}

export function useChartProps({ originData, year, externalTooltipHandler }: UseChartProps) {
  const [isInteracted, setIsInteracted] = useState<boolean>();
  const displayMode = useGetDisplayMode(year);
  const datasetsData = useGetDatasetsData(originData, year);
  const labels = useGetLabels(originData, year);
  const [pointStyle, setPointStyle] = useState<PonitStyle>(POINT_STYLE);

  useEffect(() => {
    if (year === 5) {
      setPointStyle({
        ...POINT_STYLE,
        pointRadius: 2.5,
        pointHoverRdius: 3.5,
        pointHoverBorderWidth: 2.5,
      });
    } else {
      setPointStyle(POINT_STYLE);
    }
  }, [year]);

  const data = useMemo(
    () => ({
      labels,
      datasets: [
        {
          type: 'line',
          label: '전체인원',
          data: datasetsData,
          parsing: {
            xAxisKey: 'date',
            yAxisKey: 'total',
          },
          ...LINE_STYLE,
          ...pointStyle,
        },
        {
          type: 'bar',
          label: '입사자',
          data: datasetsData,
          parsing: {
            xAxisKey: 'date',
            yAxisKey: 'hired',
          },
          backgroundColor: '#F8DDC7',
          ...DEFAULT_CHART_STYLE,
          ...getBarChartSizeProps(year, true),
        },
        {
          type: 'bar',
          label: '퇴사자',
          data: datasetsData,
          parsing: {
            xAxisKey: 'date',
            yAxisKey: 'left',
          },
          backgroundColor: '#87519B',
          ...DEFAULT_CHART_STYLE,
          ...getBarChartSizeProps(year),
        },
      ],
    }),
    [datasetsData, year, labels],
  );

  const options = useMemo(
    () => ({
      ...DEFAULT_OPTIONS,
      onHover: () => {
        if (!isInteracted) {
          setIsInteracted(true);
        }
      },
      onClick: (ctx: any) => {
        if (!isInteracted) {
          setIsInteracted(true);
        }
        const [totalPoint, hiredPoint, leftPoint] = ctx.chart.tooltip.dataPoints;

        if (totalPoint && hiredPoint && leftPoint) {
          const total = totalPoint.formattedValue;
          const hired = hiredPoint.formattedValue;
          const left = leftPoint.formattedValue;
          const date = totalPoint.label;

          setGtmDataLayer('company__personnel__graph__click', {
            date,
            totalCount: total,
            entrantCount: hired,
            leaverCount: left,
          });
        }
      },
      plugins: {
        ...DEFAULT_OPTIONS.plugins,
        tooltip: {
          enabled: false,
          external: externalTooltipHandler,
        },
      },
      scales: {
        x: {
          ...DEFAULT_OPTIONS.scales.x,
          grid: {
            ...DEFAULT_OPTIONS.scales.x.grid,
          },
          ticks: {
            ...DEFAULT_OPTIONS.scales.x.ticks,
            // @ts-ignore
            callback: (value, index) => {
              if (!displayMode) {
                return undefined;
              }

              const currentLabel = labels[index];
              const thisMonth = dayjs().get('month') + 1;
              const quarter = getQuarter(thisMonth);
              const date = dayjs(currentLabel);
              const month = date.get('month') + 1;

              if (displayMode.labelsPeriod === 3 && Math.abs(thisMonth - month) % 3 === 0) {
                return date.format('YYYY.MM');
              }

              if (displayMode.labelsPeriod === 6 && Math.abs(thisMonth - month) % 6 === 0) {
                return date.format('YYYY.MM');
              }

              //  이 경우는 데이터도 쿼터별로 그룹핑 됨 (getDataByQuarter함수 참고)
              if (displayMode.labelsPeriod === 12) {
                if (labels[index].indexOf(`${quarter}Q`) >= 0) {
                  return labels[index];
                }
              }

              return undefined;
            },
          },
        },
        y: {
          ...DEFAULT_OPTIONS.scales.y,
        },
      },
    }),
    [displayMode, labels, externalTooltipHandler],
  );

  const plugins = [TOOLTIP_LINE_PLUGINS, TOOLTIP_CONNECT_DASH];

  return { data, options, plugins, isInteracted };
}

export function useGetDatasetsData(originData: EmployeeChartData[], year: AvailableYears) {
  const { isMobile } = useMedia();
  const [data, setData] = useState<EmployeeChartData[]>();

  useEffect(() => {
    if (isMobile && year !== 1) {
      setData(getDataByQuarter(originData, year));

      return;
    }

    setData(getSliceData(originData, year));
  }, [originData, isMobile, year]);

  return data;
}

export function useGetLabels(originData: EmployeeChartData[], year: AvailableYears) {
  const [labels, setLabels] = useState<string[]>([]);
  const displayMode = useGetDisplayMode(year);

  useEffect(() => {
    if (displayMode?.labelsPeriod) {
      const slicedData = getSliceData(originData, year);
      setLabels(getLabelsByPediod(slicedData, displayMode?.labelsPeriod, year));
    }
  }, [originData, displayMode]);

  return labels;
}

/**
 *
 * @param year
 * @returns
 */
function useGetDisplayMode(year: AvailableYears) {
  const { isMobile } = useMedia();
  const [displayMode, setDisplayMode] = useState<DisplayModeProps>();

  useEffect(() => {
    if (year === 1) {
      setDisplayMode({
        labelsPeriod: 3,
        datasetDisplayMode: 'month',
      });

      return;
    }

    if (isMobile) {
      setDisplayMode({
        labelsPeriod: 12,
        datasetDisplayMode: 'quarter',
      });

      return;
    }

    setDisplayMode({
      labelsPeriod: 6,
      datasetDisplayMode: 'month',
    });
  }, [year, isMobile]);

  return displayMode;
}
